import * as React from "react";
import { Download, FileSpreadsheet, Loader2 } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@eduviaos/ui-kit";
import type { StudentImportTemplateInfoResponse } from "@eduviaos/shared";
import { DynamicColumnsPanel } from "./DynamicColumnsPanel";

/**
 * Phase H6d — shared template-download card extracted from the wizard's
 * `<UploadState>` so the standing importer at `/imports/students` can
 * surface the same row of affordances (canonical columns + dynamic
 * columns panel + download button) without re-implementing copy or
 * layout.
 *
 * The two orchestrators differ in which `download-template` HTTP route
 * the button hits (`/me/first-run/students/template.csv` for the
 * wizard, `/me/students/import-template.csv` for the standing surface)
 * and in the body copy that explains who the recipient of the
 * downloaded file is — both are wired in via props rather than a flag
 * so each surface keeps a single, readable source of truth.
 */
export interface TemplateCardProps {
  template: StudentImportTemplateInfoResponse | null;
  onDownload: () => void | Promise<void>;
  isDownloading: boolean;
  downloadError: string | null;
  /** Body copy shown under the card's title — wizard uses a longer
   *  "hand it to your office/IT vendor" pitch; the standing surface
   *  shortens it to "match the columns your roster already uses". */
  bodyCopy: React.ReactNode;
}

/** Fallback label for a column when the BE predates `canonical_column_labels`. */
function humanizeColumn(key: string): string {
  const spaced = key.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function TemplateCard({
  template,
  onDownload,
  isDownloading,
  downloadError,
  bodyCopy,
}: TemplateCardProps) {
  const canonicalColumns = template?.canonical_columns ?? [];
  const columnLabels = template?.canonical_column_labels ?? {};
  const maxRows = template?.max_rows_per_batch ?? 500;
  const hasActiveForm = template?.has_active_form ?? false;
  const dynamicColumns = template?.dynamic_columns ?? [];
  const formSlug = template?.form_slug ?? null;
  const formVersion = template?.form_version ?? null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileSpreadsheet className="size-4" aria-hidden="true" />
          Start with the recommended template
        </CardTitle>
        <CardDescription>{bodyCopy}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void onDownload()}
            disabled={isDownloading}
          >
            {isDownloading ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Preparing template…
              </>
            ) : (
              <>
                <Download className="size-4" aria-hidden="true" />
                Download CSV template
              </>
            )}
          </Button>
          <span className="text-muted-foreground text-xs">
            Up to {maxRows} rows per batch — split larger files.
          </span>
        </div>
        {downloadError ? (
          <p role="alert" className="text-destructive text-xs">
            Couldn&apos;t download template: {downloadError}
          </p>
        ) : null}
        {canonicalColumns.length > 0 ? (
          <div className="grid gap-1.5">
            <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              Recognised columns
            </p>
            <ul className="flex flex-wrap gap-1.5" data-testid="import-template-columns">
              {canonicalColumns.map((c) => (
                <li
                  key={c}
                  className="bg-muted text-foreground/80 rounded-sm px-1.5 py-0.5 text-[11px]"
                >
                  {columnLabels[c] ?? humanizeColumn(c)}
                </li>
              ))}
            </ul>
            <p className="text-muted-foreground mt-1 text-[11px] leading-relaxed">
              Common alternate spellings of these column names are matched automatically.
            </p>
          </div>
        ) : null}

        {hasActiveForm && dynamicColumns.length > 0 ? (
          <DynamicColumnsPanel
            dynamicColumns={dynamicColumns}
            formSlug={formSlug}
            formVersion={formVersion}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}
